import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { marked } from "marked";
import TranslateIcon from "../../assets/svg/translate-icon";
import VerbamessengerIcon from "../../assets/svg/verbamessenger-icon";
import VerbamailIcon from "../../assets/svg/verbamail-icon";
import VerbacallIcon from "../../assets/svg/verbacall-icon";
import VerbachatIcon from "../../assets/svg/verbachat-icon";
import ClockIcon from "../../assets/svg/clock-icon";
import VerbafloLogoIcon from "../../assets/images/verbaflo-logo.png";
import UserIcon from "../../assets/svg/user-icon";
import { Avatar } from "../Avatar/Avatar";
import { isHTML } from "../../pages/Communications/utils/functions";
import {
  processHtmlContent,
  addTargetBlankToLinks,
  convertPhoneNumbersToLinks,
} from "../../utils/functions/formatters";
import { getDateTimeByFormat } from "../../redux/slices/dateTimeFormatSlice";
import { Tooltip } from "../Tooltip/Tooltip";
import AttachmentPreviewModal from "../../pages/Communications/components/CommunicationDetailsPanel/components/AttachmentPreviewModal";
import {
  Attachment,
  LeadDetails,
  Message,
} from "../../pages/Communications/utils/types";
import AttachmentCarousel from "../Common/AttachmentCarousel";
import EmailBodyIframe from "./EmailBodyIframe";
import { AttachmentItem } from "../Common/AttachmentInput";
import { markdownBreakFormatter } from "../../utils/functions/formatters";
import { useSearchHighlight } from "../../context/SearchHighlightContext";
import { highlightTextInHtml } from "../../pages/Communications/utils/highlightSearchText";
import MentionsIcon from "../../assets/svg/mentions-icon";
import ChevronDownIcon from "../../assets/svg/chevron-down-icon";
import { format } from "date-fns";
import TicketIcon from "../../assets/svg/ticket-icon";
import BulbIcon from "../../assets/svg/bulb-icon";
import VerbasmsIcon from "../../assets/svg/verbasms-icon";
import FbMessengerIcon from "../../assets/svg/fb-messenger-icon";
import InstagramIcon from "../../assets/svg/instagram-icon";
import {
  useFlagPlaygroundResponseMutation,
  useUnflagPlaygroundResponseMutation,
} from "../../redux/services/playgroundApi";
import { customToast } from "../../utils/custom-toast/custom-toast";
import FlagIcon from "../../assets/svg/flag-icon";
import FlaggedIcon from "../../assets/svg/flagged-icon";
import FlagFormModal from "../../pages/Playground/components/Testing/components/FlagFormModal";
import ChevronRightIcon from "../../assets/svg/chevron-right-icon";
import Button from "../Button/Button";
import { trackEvents } from "../../utils/amplitude/amplitude";
import MessageStatusIndicator from "../MessageStatusIndicator/MessageStatusIndicator";
import { getMessageWidthClasses } from "./TextMessageBlock.helpers";

type Modality = "email" | "call" | "messenger" | "chat" | "mention" | string;

interface TextMessageBlockProps {
  message: string;
  translatedMessage?: string;
  modality: Modality;
  isSent?: boolean;
  showTranslate?: boolean;
  showAiResponseTime?: boolean;
  showTimestamp?: boolean;
  showDotAfterTimestamp?: boolean;
  iconType?: "verbaflo" | "participant" | "agent";
  timestamp?: string;
  participantInfo?: {
    text: string;
    fullName?: string;
    size?: number;
    showOnlineStatus?: boolean;
    isOnline?: boolean;
  };
  aiResponseTime?: number;
  showAllTranslation?: boolean;
  messageId?: string;
  showModality?: boolean;
  showHoverEffect?: boolean;
  attachments?: Attachment[];
  showSourceButton?: boolean;
  onGetSources?: (message_id: string) => void;
  regularMessage: Message;
  leadDetails: LeadDetails | null;
  isTicket?: boolean;
  leadId?: string;
  showFlagDot?: boolean;
  module?: "communication" | "crm" | "campaign" | "playground";
}

const TextMessageBlock: React.FC<TextMessageBlockProps> = ({
  onGetSources,
  message,
  translatedMessage,
  modality,
  isSent = false,
  showTranslate = false,
  showAiResponseTime = false,
  showTimestamp = true,
  showDotAfterTimestamp = false,
  iconType,
  timestamp,
  participantInfo,
  aiResponseTime,
  showAllTranslation = false,
  messageId,
  showModality = true,
  showHoverEffect = false,
  attachments,
  showSourceButton = false,
  regularMessage,
  leadDetails,
  isTicket = false,
  leadId,
  showFlagDot = true,
  module = "communication",
}) => {
  const [showOriginalContent, setShowOriginalContent] = useState(true);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [initialIndex, setInitialIndex] = useState(0);
  const [isEmailDetailsOpen, setIsEmailDetailsOpen] = useState(false);
  const emailDetailsRef = useRef<HTMLDivElement>(null);
  const [isFlagged, setIsFlagged] = useState(
    !!regularMessage?.flagged_metadata
  );
  const [isFlagDetailsOpen, setIsFlagDetailsOpen] = useState(false);
  const [flagModalOpen, setFlagModalOpen] = useState(false);

  const [flagPlaygroundResponse, { isLoading: isFlagging }] =
    useFlagPlaygroundResponseMutation();

  const [unflagPlaygroundResponse, { isLoading: isUnflagging }] =
    useUnflagPlaygroundResponseMutation();

  const { searchQuery } = useSearchHighlight();

  useEffect(() => {
    setShowOriginalContent(!showAllTranslation);
  }, [showAllTranslation]);

  useEffect(() => {
    if (!isEmailDetailsOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        emailDetailsRef.current &&
        !emailDetailsRef.current.contains(e.target as Node)
      ) {
        setIsEmailDetailsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEmailDetailsOpen]);

  const attachmentsWithTimestamp = useMemo(() => {
    return (attachments || []).map((attachment) => ({
      ...attachment,
      timestamp: attachment.timestamp || timestamp || "",
    }));
  }, [attachments, timestamp]);

  const modalityConfig = {
    email: {
      icon: VerbamailIcon,
      label: "VerbaMail",
      color: "text-verba-mail dark:text-verba-mail-dk",
    },
    call: {
      icon: VerbacallIcon,
      label: "VerbaCall",
      color: "text-verba-lavender dark:text-verba-lavender-dk",
    },
    messenger: {
      icon: VerbamessengerIcon,
      label: "VerbaMessenger",
      color: "text-verba-messenger dark:text-verba-messenger-dk",
    },
    chat: {
      icon: VerbachatIcon,
      label: "VerbaChat",
      color: "text-verba-chat dark:text-verba-chat-dk",
    },
    sms: {
      icon: VerbasmsIcon,
      label: "VerbaSMS",
      color: "text-verba-sms dark:text-verba-sms-dk",
    },
    fb_messenger: {
      icon: FbMessengerIcon,
      label: "Facebook Messenger",
      color: "text-verba-fb-messenger dark:text-verba-fb-messenger-dk",
    },
    instagram: {
      icon: InstagramIcon,
      label: "Instagram DMs",
      color: "",
    },
    mentions: {
      icon: MentionsIcon,
      label: "Mentions",
      color: "text-verba-indigo dark:text-verba-indigo-dk",
    },
  };

  const currModalityConfig =
    modalityConfig[modality as keyof typeof modalityConfig] || null;

  const handleTranslateClick = () => {
    if (translatedMessage) {
      setShowOriginalContent(!showOriginalContent);
    }
  };

  const rawMessage =
    translatedMessage && translatedMessage.length > 0 && !showOriginalContent
      ? translatedMessage || message
      : message;

  const buttonText = showOriginalContent ? "See Translation" : "Show Original";

  const isFullHtmlEmail =
    modality === "email" && !!rawMessage && isHTML(rawMessage);

  const processedContent = useMemo(() => {
    if (!rawMessage) return "";

    const parseContent = (): string => {
      if (isFullHtmlEmail) return rawMessage;

      const formatted =
        markdownBreakFormatter(rawMessage.replace(/\\n/g, "\n")) ?? "";

      if (!isHTML(rawMessage)) {
        const listPattern = /^\s*(?:[-*+]|\d+[.)]\s)/;
        const lines = formatted.split("\n");
        const result: string[] = [lines[0]];
        for (let i = 1; i < lines.length; i++) {
          const prevRaw = lines[i - 1].replace(/<br>/g, "");
          const currRaw = lines[i].replace(/<br>/g, "");
          if (listPattern.test(prevRaw) && !listPattern.test(currRaw) && currRaw.trim() !== "") {
            result.push("", lines[i]);
          } else {
            result.push(lines[i]);
          }
        }
        const parsed = String(marked(result.join("\n")));
        return modality === "call" ? parsed : addTargetBlankToLinks(parsed);
      }

      return processHtmlContent(formatted);
    };

    let content = parseContent();

    if (!isFullHtmlEmail) {
      content = convertPhoneNumbersToLinks(content);
    }

    if (searchQuery?.trim()) {
      content = highlightTextInHtml(content, searchQuery);
    }

    return content;
  }, [rawMessage, searchQuery, isFullHtmlEmail, modality]);

  const renderIcon = () => {
    if (!iconType) return null;

    return (
      <>
        {iconType === "verbaflo" && (
          <div>
            <img
              src={VerbafloLogoIcon}
              alt="Verbaflo Logo"
              className="w-[24px] h-[24px]"
            />
          </div>
        )}
        {iconType === "participant" && participantInfo && (
          <div
            className={`flex items-center gap-2 ${
              isSent ? "flex-row-reverse" : "flex-row"
            }`}
          >
            <div className="shrink-0 relative">
              <Avatar
                size={participantInfo.size || 24}
                text={participantInfo.text}
                className="shrink-0"
              />
              {participantInfo.showOnlineStatus && (
                <div className="w-[6px] h-[6px] bg-pure-white dark:bg-pure-white-dk rounded-full absolute bottom-0 right-0 flex flex-col items-center justify-center">
                  <div
                    className={`w-[4px] h-[4px] ${
                      participantInfo.isOnline
                        ? "bg-verba-green-success dark:bg-verba-green-success-dk"
                        : "bg-offline dark:bg-offline-dk"
                    } rounded-full`}
                  ></div>
                </div>
              )}
            </div>
            {participantInfo.fullName && (
              <span className="text-sm truncate max-w-28 font-medium text-heading-text dark:text-heading-text-dk">
                {participantInfo.fullName}
              </span>
            )}
          </div>
        )}
        {iconType === "agent" && (
          <div className="flex items-center gap-2">
            {participantInfo?.fullName && (
              <span className="text-sm truncate max-w-28 font-medium text-heading-text dark:text-heading-text-dk">
                {participantInfo?.fullName}
              </span>
            )}
            <UserIcon className="w-[9px] h-[13px] text-verba-indigo dark:text-verba-indigo-dk" />
          </div>
        )}
      </>
    );
  };

  const handlePreview = (index: number) => {
    if (!attachments || !attachments[index]) return;
    setInitialIndex(index);
    setIsPreviewModalOpen(true);
  };

  const carouselItems: AttachmentItem[] = useMemo(() => {
    if (!attachments || attachments.length === 0) return [];
    return attachments.map((attachment, index) => ({
      id: (attachment.s3_key as string) || String(index),
      file: null,
      name: attachment.file_name as string,
      mime: attachment.content_type as string,
      previewUrl: attachment.s3_url as string,
      status: "sent" as const,
    }));
  }, [attachments]);

  const handleCarouselPreview = (item: AttachmentItem, index?: number) => {
    if (index !== undefined) {
      handlePreview(index);
    }
  };

  const handleEmailIframeReady = useCallback((iframe: HTMLIFrameElement) => {
    requestAnimationFrame(() => {
      const scrollContainer = iframe.closest(
        ".overflow-y-auto, .overflow-y-scroll"
      );
      if (scrollContainer) {
        scrollContainer.scrollTo({
          top: scrollContainer.scrollHeight,
          behavior: "smooth",
        });
      }
    });
  }, []);

  const renderAttachments = () => {
    if (!attachments || attachments.length === 0) return null;

    return (
      <>
        <AttachmentCarousel
          items={carouselItems}
          onPreview={handleCarouselPreview}
          isSent={isSent}
          onRemove={() => {}}
          hideDeleteButton={true}
        />
      </>
    );
  };

  const handleFlagPlaygroundResponse = async ({
    isFlagged,
    isLiked,
    isDisliked,
    reason,
    additionalDetails,
    shouldRevert,
  }: {
    isFlagged?: boolean;
    isLiked?: boolean;
    isDisliked?: boolean;
    reason?: string;
    additionalDetails?: string;
    shouldRevert?: boolean;
  }) => {
    if (!leadId || !messageId) {
      console.error("Missing leadId or messageId");
      return;
    }

    try {
      const response = await flagPlaygroundResponse({
        leadId: leadId || "",
        messageId: messageId,
        ...(isFlagged !== undefined && { flag: isFlagged }),
        ...(isLiked !== undefined && { like: isLiked }),
        ...(isDisliked !== undefined && { dislike: isDisliked }),
        ...(reason && { reason }),
        ...(additionalDetails && { additional_details: additionalDetails }),
        // ...(shouldRevert !== undefined && { should_revert: shouldRevert }),
        isPlaygroundTesting: false,
      }).unwrap();

      return response;
    } catch (error) {
      console.error("Error flagging response:", error);
      throw error;
    }
  };

  const handleFlagSubmit = async (data: {
    reason: string;
    additionalDetails: string;
  }) => {
    try {
      await handleFlagPlaygroundResponse({
        isFlagged: true,
        reason: data.reason,
        additionalDetails: data.additionalDetails,
        shouldRevert: false, // false when flagging (setting to true)
      });
      setIsFlagged(true);
      setFlagModalOpen(false);
      trackEvents("message_flagged", {
        message_id: messageId || "",
        lead_id: leadId || "",
        module,
        modality: modality || "unknown",
        reason: data.reason,
        timestamp: new Date().toISOString(),
      });
      customToast("Response flagged successfully", "success");
    } catch (error) {
      console.error("Error flagging response:", error);
    }
  };

  const handleUnflag = async () => {
    try {
      await unflagPlaygroundResponse({
        flag_id: regularMessage?.flagged_metadata?.id || "",
        isPlayground: false,
      });
      setIsFlagged(false);
      setIsFlagDetailsOpen(false);
      trackEvents("message_unflagged", {
        message_id: messageId || "",
        lead_id: leadId || "",
        module,
        modality: modality || "unknown",
        flag_id: regularMessage?.flagged_metadata?.id || "",
        timestamp: new Date().toISOString(),
      });
      customToast("Response unflagged successfully", "success");
    } catch (error) {
      console.error("Error unflagging response:", error);
    }
  };

  return (
    <div
      className={`w-fit flex flex-col gap-[4px] ${
        isSent ? "ml-auto" : "mr-auto"
      }`}
      data-message-id={messageId}
    >
      <div
        className={`flex flex-col gap-[10px] px-[12px] py-[10px] ${getMessageWidthClasses(modality)} ${
          isSent
            ? isFlagged
              ? `ml-auto border border-tag-red-stroke dark:border-tag-red-stroke-dk bg-user-management-red-card-bg dark:bg-user-management-red-card-bg-dk rounded-t-[8px] rounded-bl-[8px]`
              : isTicket
                ? "ml-auto  bg-ticket-card dark:bg-ticket-card-dk rounded-t-[8px] rounded-bl-[8px] border border-ticket-card dark:border-ticket-card-dk"
                : "ml-auto  bg-verba-chat-bubble dark:bg-verba-chat-bubble-dk rounded-t-[8px] rounded-bl-[8px]"
            : `mr-auto ${
                showHoverEffect
                  ? "bg-hover-effect dark:bg-hover-effect-dk"
                  : "bg-gray-50 dark:bg-gray-50-dk"
              }  rounded-t-[8px] rounded-br-[8px]`
        } message-card`}
      >
        {modality === "email" && isSent && (
          <div className="flex items-center justify-between mb-2 gap-2">
            <div className="flex items-center gap-[4px]">
              <VerbamailIcon className="w-[18px] h-[18px] text-verba-mail dark:text-verba-mail-dk" />

              <p className="font-medium text-md text-heading-text dark:text-heading-text-dk">
                {`VerbaMail`}
              </p>
            </div>

            {modality === "email" &&
              leadDetails &&
              leadDetails?.name &&
              isSent && (
                <div ref={emailDetailsRef} className="relative">
                  <div className="flex items-center gap-[4px]">
                    <p className="text-dark-gray dark:text-dark-gray-dk text-md font-normal">
                      to {leadDetails?.name}
                    </p>
                    <button
                      onClick={() => setIsEmailDetailsOpen(!isEmailDetailsOpen)}
                    >
                      <ChevronDownIcon
                        className={`w-[10px] h-[10px] text-verba-indigo dark:text-verba-indigo-dk transition-transform ${
                          isEmailDetailsOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  </div>

                  {isEmailDetailsOpen && (
                    <div className="flex flex-col gap-[6px] min-w-[300px] px-[12px] py-[12px] border border-border-gray dark:border-border-gray-dk rounded-[8px] shadow-popper-blur-effect backdrop-blur-[40px] absolute top-5 right-0 bg-tooltip-bg dark:bg-tooltip-bg-dk z-10">
                      <div className="flex items-start gap-[8px]">
                        <p className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk font-normal">
                          From:
                        </p>
                        <p className="text-pure-white dark:text-pure-white-dk text-md font-normal break-all">
                          <span className="font-medium">
                            {regularMessage?.contact_info?.name ||
                              "VerbaFlo AI"}
                          </span>{" "}
                          {`<${regularMessage?.contact_info?.email || "support@verbaflo.ai"}>`}
                        </p>
                      </div>
                      {leadDetails?.name && leadDetails?.email && (
                        <div className="flex items-start gap-[8px]">
                          <p className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk font-normal">
                            To:
                          </p>
                          <p className="text-pure-white dark:text-pure-white-dk text-md font-normal">
                            <span className="font-medium">
                              {leadDetails?.name}
                            </span>{" "}
                            {`<${leadDetails?.email}>`}
                          </p>
                        </div>
                      )}
                      {regularMessage?.cc && regularMessage?.cc.length > 0 && (
                        <div className="flex items-start gap-[8px]">
                          <p className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk font-normal">
                            Cc:
                          </p>
                          {regularMessage?.cc &&
                            regularMessage?.cc.length > 0 && (
                              <div className="flex flex-col gap-[2px] text-pure-white dark:text-pure-white-dk text-md font-normal">
                                {regularMessage?.cc.map((cc) => (
                                  <span key={cc}>{`<${cc}>`}</span>
                                ))}
                              </div>
                            )}
                        </div>
                      )}
                      {regularMessage?.bcc &&
                        regularMessage?.bcc.length > 0 && (
                          <div className="flex items-start gap-[8px]">
                            <p className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk font-normal">
                              Bcc:
                            </p>
                            <div className="flex flex-col gap-[2px] text-pure-white dark:text-pure-white-dk text-md font-normal">
                              {regularMessage?.bcc.map((bcc) => (
                                <span key={bcc}>{`<${bcc}>`}</span>
                              ))}
                            </div>
                          </div>
                        )}

                      {regularMessage?.timestamp && (
                        <div className="flex items-start gap-[8px]">
                          <p className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk font-normal">
                            Timestamp:
                          </p>
                          <p className="text-pure-white dark:text-pure-white-dk text-md font-normal">
                            {format(
                              new Date(regularMessage?.timestamp || ""),
                              "dd MMM, yyyy HH:mm a"
                            )}
                          </p>
                        </div>
                      )}

                      {regularMessage?.subject && (
                        <div className="flex items-start gap-[8px]">
                          <p className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk font-normal">
                            Subject:
                          </p>
                          <p className="text-pure-white dark:text-pure-white-dk text-md font-normal">
                            {regularMessage?.subject || ""}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
          </div>
        )}

        {modality === "email" && (
          <div className="flex flex-col gap-[2px]">
            {regularMessage?.subject && isSent && (
              <span className="text-xxs uppercase font-medium text-gray-placeholder dark:text-gray-placeholder-dk">
                Subject
              </span>
            )}
            <span className="text-sm font-normal text-heading-text dark:text-heading-text-dk">
              {regularMessage?.subject || ""}
            </span>
          </div>
        )}

        {isTicket && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-[4px]">
              <TicketIcon className="w-[18px] h-[18px] text-lead-stage-engaged dark:text-lead-stage-engaged-dk" />
              <span className="text-md font-medium text-heading-text dark:text-heading-text-dk">
                Ticket Created
              </span>
            </div>
            <div className="flex items-center gap-[2px]">
              <img
                src={VerbafloLogoIcon}
                alt="Verbaflo Logo"
                className="w-[18px] h-[18px]"
              />
              <p className="text-verba-indigo dark:text-verba-indigo-dk text-xs font-bold">
                VerbaFlo.
              </p>
            </div>
          </div>
        )}
        {message && (
          <div className="flex flex-col gap-[2px]">
            {modality === "email" && isSent && (
              <span className="text-xxs uppercase font-medium text-gray-placeholder dark:text-gray-placeholder-dk">
                Body
              </span>
            )}
            {isFullHtmlEmail ? (
              <EmailBodyIframe
                html={processedContent}
                onContentReady={handleEmailIframeReady}
              />
            ) : (
              <div
                className="
                  text-md text-heading-text dark:text-heading-text-dk font-normal
                  break-words overflow-wrap-anywhere
                  [&_*]:break-words [&_*]:overflow-wrap-anywhere
                  [&_p]:mb-2 [&_p:last-child]:mb-0
                  [&_ul]:mb-2 [&_ol]:mb-2
                  [&_ul]:list-disc [&_ul]:pl-6
                  [&_ol]:list-decimal [&_ol]:pl-6
                  [&_li]:mb-1
                  [&_h1]:mb-2 [&_h2]:mb-2 [&_h3]:mb-2 [&_h4]:mb-2 [&_h5]:mb-2 [&_h6]:mb-2
                  [&_code]:bg-gray-100 [&_code]:dark:bg-gray-800 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded
                  [&_blockquote]:mb-2 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:dark:border-gray-600 [&_blockquote]:pl-4 [&_blockquote]:italic
                  [&_pre]:mb-2 [&_pre]:whitespace-pre-wrap [&_pre]:break-words [&_pre]:overflow-wrap-anywhere [&_pre]:bg-gray-100 [&_pre]:dark:bg-gray-800 [&_pre]:p-3 [&_pre]:rounded [&_pre]:overflow-x-auto
                  [&_pre>code]:bg-transparent [&_pre>code]:p-0
                "
                dangerouslySetInnerHTML={{
                  __html: processedContent,
                }}
              />
            )}
            {isSent && (
              <div
                className={`flex flex-col gap-4.5 overflow-hidden transition-all duration-500 ease-in-out ${
                  isFlagDetailsOpen && isSent
                    ? "opacity-100 mt-4"
                    : "opacity-0 h-0"
                }`}
              >
                <hr className="border-user-management-red-card-bg dark:border-user-management-red-card-bg-dk" />
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-dark-gray dark:text-dark-gray-dk">
                    Reason
                  </span>
                  <span className="text-sm font-normal text-heading-text dark:text-heading-text-dk">
                    {regularMessage?.flagged_metadata?.reason ||
                      "No reason provided"}
                  </span>
                </div>
                {regularMessage?.flagged_metadata?.additional_details && (
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-dark-gray dark:text-dark-gray-dk">
                      Additional Details
                    </span>
                    <span className="text-sm font-normal text-heading-text dark:text-heading-text-dk">
                      {regularMessage.flagged_metadata.additional_details}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {regularMessage?.flagged_metadata?.flagged_by?.user_name && (
                    <>
                      <Avatar
                        size={24}
                        text={
                          regularMessage.flagged_metadata?.flagged_by?.user_name
                        }
                        className="shrink-0"
                      />
                      <span className="text-sm font-normal text-heading-text dark:text-heading-text-dk">
                        {
                          regularMessage?.flagged_metadata?.flagged_by
                            ?.user_name
                        }
                      </span>
                    </>
                  )}
                  {regularMessage?.flagged_metadata?.flagged_at && (
                    <>
                      {regularMessage?.flagged_metadata?.flagged_by
                        ?.user_name &&
                        regularMessage?.flagged_metadata?.flagged_at && (
                          <div className="w-px h-[10px] bg-gray-placeholder dark:bg-gray-placeholder-dk"></div>
                        )}
                      <span className="text-xs font-normal text-dark-gray dark:text-dark-gray-dk">
                        {format(
                          new Date(
                            regularMessage?.flagged_metadata?.flagged_at
                          ),
                          "dd MMM yyyy, h:mm a"
                        )}
                      </span>
                    </>
                  )}
                </div>
                <Button
                  innerText="Unflag this response"
                  buttonContainerClass="px-4 py-1 w-fit rounded-md border border-verba-indigo dark:border-verba-indigo-dk text-verba-indigo dark:text-verba-indigo-dk hover:text-pure-white hover:dark:text-pure-white-dk hover:bg-verba-indigo dark:hover:bg-verba-indigo-dk"
                  innerTextClass="text-md font-semibold"
                  onClick={handleUnflag}
                  disabled={isUnflagging}
                />
                <hr className="border-user-management-red-card-bg dark:border-user-management-red-card-bg-dk" />
              </div>
            )}

            {isFlagged && isSent && (
              <div className="flex items-center justify-between mt-4.5 gap-2">
                <div className="flex items-center justify-between gap-[4px] border border-gray-placeholder dark:border-gray-placeholder-dk bg-pure-white dark:bg-pure-white-dk rounded-md py-0.5 px-1.5 w-fit">
                  <FlaggedIcon className="w-[18px] h-[18px] text-verba-red-alert dark:text-verba-red-alert-dk" />
                  <p className="text-md font-medium text-verba-red-alert dark:text-verba-red-alert-dk">
                    Flagged
                  </p>
                </div>
                <Button
                  innerText={
                    isFlagDetailsOpen ? "Hide Details" : "View Details"
                  }
                  buttonContainerClass="text-verba-blue dark:text-verba-blue-dk"
                  secondaryButtonContainerClass="!gap-[2px]"
                  innerTextClass="text-md font-medium"
                  Icon={ChevronRightIcon}
                  iconClass={`w-[14px] h-[14px] transition-transform  ${
                    !isFlagDetailsOpen ? "rotate-90" : "rotate-270"
                  }`}
                  onClick={() => setIsFlagDetailsOpen((prev) => !prev)}
                />
              </div>
            )}
          </div>
        )}

        {attachments && attachments?.length > 0 && (
          <div className="flex flex-col gap-[2px]">
            {modality === "email" &&
              attachments &&
              attachments.length > 0 &&
              isSent && (
                <span className="text-xxs uppercase font-medium text-gray-placeholder dark:text-gray-placeholder-dk">
                  Attachments
                </span>
              )}
            {renderAttachments()}
          </div>
        )}

        {showTranslate && translatedMessage && (
          <button
            className="w-max flex items-center gap-[4px] text-md font-semibold text-verba-blue-dark-mode dark:text-verba-blue-dark-mode-dk hover:underline"
            onClick={handleTranslateClick}
          >
            <TranslateIcon className="w-[14px] h-[14px]" />
            <p>{buttonText}</p>
          </button>
        )}
      </div>
      <div
        className={`flex items-center justify-end gap-[4px] ${
          isSent ? "flex-row" : "flex-row-reverse"
        }`}
      >
        {showModality && currModalityConfig && (
          <div className="flex items-center gap-[2px]">
            <currModalityConfig.icon
              className={`w-[14px] h-[14px] ${currModalityConfig.color}`}
            />
            {isSent && (
              <p className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
                {currModalityConfig.label}
              </p>
            )}
          </div>
        )}
        {showAiResponseTime && aiResponseTime && aiResponseTime > 0 && (
          <>
            <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full" />
            <Tooltip content="AI Response Time">
              <div className="flex items-center gap-[2px]">
                <ClockIcon className="w-[11px] h-[11px] text-gray-placeholder dark:text-gray-placeholder-dk" />
                <p className="text-sm font-medium text-gray-placeholder dark:text-gray-placeholder-dk leading-none cursor-pointer">
                  {aiResponseTime?.toFixed(1)}s
                </p>
              </div>
            </Tooltip>
          </>
        )}
        {showTimestamp && timestamp && (
          <>
            {!showDotAfterTimestamp && (
              <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full" />
            )}
            <div>
              <p className="text-sm font-medium text-gray-placeholder dark:text-gray-placeholder-dk">
                {getDateTimeByFormat(new Date(timestamp || "")).time}
              </p>
            </div>
            {showDotAfterTimestamp && (
              <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full" />
            )}
          </>
        )}
        {isSent && (
            <>
              <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full" />
              <MessageStatusIndicator
                statusHistory={regularMessage?.status_history || []}
              />
            </>
          )}
        {showSourceButton && (
          <>
            <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full" />
            <Tooltip content="View Source">
              <button
                className="hover:text-verba-blue dark:hover:text-verba-blue-dk transition-colors  flex items-center justify-center"
                onClick={() => onGetSources && onGetSources(messageId || "")}
              >
                <BulbIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk mb-1" />
              </button>
            </Tooltip>
          </>
        )}
        {isSent && regularMessage?.role !== "agent" && (
          <>
            {showFlagDot && (
              <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full" />
            )}
            {!isFlagged ? (
              <Tooltip content="Flag this response">
                <button
                  className="flex items-center justify-center disabled:opacity-50"
                  onClick={() => setFlagModalOpen(true)}
                  disabled={isFlagging}
                >
                  <FlagIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
                </button>
              </Tooltip>
            ) : (
              <button
                className="flex items-center justify-center"
                onClick={() => setIsFlagDetailsOpen((prev) => !prev)}
              >
                <FlaggedIcon className="w-[18px] h-[18px] text-verba-red-alert dark:text-verba-red-alert-dk" />
              </button>
            )}
          </>
        )}

        {iconType && (
          <>
            {showModality && (
              <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full" />
            )}
            {renderIcon()}
          </>
        )}
      </div>

      {/* Attachment Preview Modal */}

      <AttachmentPreviewModal
        open={isPreviewModalOpen}
        onOpenChange={setIsPreviewModalOpen}
        attachments={attachmentsWithTimestamp}
        initialIndex={initialIndex}
      />

      <FlagFormModal
        open={flagModalOpen}
        onOpenChange={setFlagModalOpen}
        messageContent={message}
        modality={modality}
        onSubmit={handleFlagSubmit}
        isSubmitting={isFlagging}
        attachments={attachments}
      />
    </div>
  );
};

export default TextMessageBlock;
